import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../hooks/useTranslation';
import ukFlag from '../assets/flags/uk.svg';
import hrFlag from '../assets/flags/hr.svg';
import deFlag from '../assets/flags/de.svg';
import frFlag from '../assets/flags/fr.svg';

/**
 * Enhanced Language switcher component with dropdown and circular flag
 */
const LanguageSwitcher = () => {
  const { language, setSpecificLanguage } = useLanguage();
  const t = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Language options with their display names and flags
  const languages = [
    { code: 'en', name: 'English', flag: ukFlag, available: true },
    { code: 'hr', name: 'Hrvatski', flag: hrFlag, available: true },
    { code: 'de', name: 'Deutsch', flag: deFlag, available: true },
    { code: 'fr', name: 'Français', flag: frFlag, available: true }
  ];
  
  // Find current language data
  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  
  // Select a language
  const selectLanguage = (langCode) => {
    setSpecificLanguage(langCode);
    setIsOpen(false);
  };
  
  return (
    <div className="relative ml-2 z-50" ref={dropdownRef}>
      <button 
        className="flex items-center justify-center w-8 h-8 rounded-full bg-transparent border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('language.switchTo')}
        title={currentLanguage.name}
      >
        <img 
          src={currentLanguage.flag} 
          alt={`${currentLanguage.name} flag`} 
          className="w-full h-full object-cover"
        />
      </button>
      
      {isOpen && (
        <ul className="absolute top-full right-0 w-32 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded shadow-md py-1 mt-1 list-none">
          {languages.map((lang) => (
            <li key={lang.code}>
              <button 
                className={`flex items-center w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                  lang.code === language ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : ''
                }`}
                onClick={() => selectLanguage(lang.code)}
                aria-selected={lang.code === language}
              >
                <img 
                  src={lang.flag} 
                  alt={`${lang.name} flag`} 
                  className="w-6 h-4 object-cover"
                />
                <span className="ml-2 text-sm text-gray-800 dark:text-gray-200">{lang.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
